'use client'

import { useId } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { ChevronDown, ChevronUp, Cookie, Shield, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/encuentro/ui/switch'
import { useCookieConsentBanner } from '@/hooks/use-cookie-consent'

/**
 * The cookie-consent banner in Encuentro (the root layout mounts it instead of
 * the classic one when `data-ui="encuentro"` is on `<html>`). Same copy, same
 * choices and the same effects as the classic banner (`useCookieConsentBanner`);
 * only the look changes: a `surface` card with `radius-lg` and `shadow-overlay`,
 * the F4 buttons and the Encuentro `Switch` (ink when on, 44 px target). It sits above the TabBar (its height is published in
 * `--mobile-bottom-nav-height`), never over it, and scrolls inside itself when
 * the text is large.
 */
export function EncuentroCookieConsentBanner() {
  const t = useTranslations('encuentro.frame.cookies')
  const consent = useCookieConsentBanner()
  const titleId = useId()
  const detailsId = useId()

  if (!consent.isVisible) return null
  const { showDetails, preferences } = consent

  return (
    <div
      data-slot="cookie-consent"
      data-look="encuentro"
      className="pointer-events-none fixed inset-x-0 bottom-[max(var(--mobile-bottom-nav-height,0px),max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px)))] z-50 flex justify-center p-0 sm:p-4 font-en-sans text-en-ink motion-safe:animate-in motion-safe:duration-300 motion-safe:slide-in-from-bottom"
    >
      <section
        aria-labelledby={titleId}
        className="pointer-events-auto grid max-h-[calc(var(--app-height,100dvh)-var(--mobile-bottom-nav-height,0px)-var(--en-size-topbar)-2rem)] w-full max-w-2xl grid-cols-[minmax(0,1fr)] gap-1 overflow-y-auto overscroll-contain rounded-en-lg bg-en-surface px-3 py-2 shadow-en-overlay max-sm:rounded-b-none sm:gap-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          {/* On the phone the title needs the width (text at 200 %): no icon there. */}
          <Cookie aria-hidden className="mt-1 hidden size-5 shrink-0 text-en-ink-muted sm:block" />
          <p
            id={titleId}
            className="min-w-0 flex-1 text-en-body-strong [overflow-wrap:break-word] hyphens-auto sm:text-en-subheading"
          >
            {t('title')}
          </p>
          <Button
            look="encuentro"
            variant="ghost"
            iconOnly
            aria-label={t('close')}
            onClick={consent.close}
            className="-my-2 -mr-2 sm:mb-0"
          >
            <X aria-hidden />
          </Button>
        </div>

        {/* On the phone the closed banner keeps to one short line so the form behind it stays in view at 375x667 (ALU-678). */}
        {!showDetails && (
          <p className="text-en-meta text-en-ink-muted sm:hidden">{t('descriptionCompact')}</p>
        )}
        <p className={`text-en-body text-en-ink-muted ${showDetails ? '' : 'max-sm:hidden'}`}>
          {t('description')}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-x-2 max-sm:-my-1">
          <Button
            look="encuentro"
            variant="ghost"
            size="sm"
            aria-expanded={showDetails}
            aria-controls={showDetails ? detailsId : undefined}
            aria-label={showDetails ? undefined : t('showDetails')}
            onClick={consent.toggleDetails}
            className="-ml-3"
          >
            {showDetails ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
            {showDetails ? (
              t('hideDetails')
            ) : (
              <>
                <span className="sm:hidden" aria-hidden>
                  {t('showDetailsShort')}
                </span>
                <span className="max-sm:hidden">{t('showDetails')}</span>
              </>
            )}
          </Button>
          <Link
            href="/policies/privacy"
            target="_blank"
            className="relative inline-flex min-h-9 items-center justify-center rounded-en-md px-2 text-en-meta text-en-ink-muted underline-offset-4 after:absolute after:inset-x-0 after:-inset-y-1 after:content-[''] hover:text-en-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
          >
            {t('privacyPolicy')}
          </Link>
        </div>

        {showDetails && (
          <div id={detailsId} className="grid gap-4 border-t border-en-line pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="grid min-w-0 gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Shield aria-hidden className="size-4 shrink-0 text-en-positive" />
                  <p className="text-en-body-strong">{t('essential')}</p>
                  <Badge look="encuentro" tone="positive">
                    {t('alwaysOn')}
                  </Badge>
                </div>
                <p className="text-en-meta text-en-ink-muted">{t('essentialDescription')}</p>
              </div>
              <Switch checked disabled onCheckedChange={() => {}} aria-label={t('essential')} />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="grid min-w-0 gap-1">
                <label htmlFor={`${detailsId}-functional`} className="text-en-body-strong">
                  {t('functional')}
                </label>
                <p className="text-en-meta text-en-ink-muted">{t('functionalDescription')}</p>
              </div>
              <Switch
                id={`${detailsId}-functional`}
                checked={preferences.functional}
                onCheckedChange={(checked) => consent.setPreference('functional', checked)}
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="grid min-w-0 gap-1">
                <label htmlFor={`${detailsId}-analytics`} className="text-en-body-strong">
                  {t('analytics')}
                </label>
                <p className="text-en-meta text-en-ink-muted">{t('analyticsDescription')}</p>
              </div>
              <Switch
                id={`${detailsId}-analytics`}
                checked={preferences.analytics}
                onCheckedChange={(checked) => consent.setPreference('analytics', checked)}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 max-sm:mt-1 sm:flex sm:flex-wrap sm:items-center">
          <Button
            look="encuentro"
            variant="secondary"
            onClick={consent.rejectNonEssential}
            aria-label={showDetails ? undefined : t('rejectNonEssential')}
            className="max-sm:px-3"
          >
            {showDetails ? (
              t('essentialOnly')
            ) : (
              <>
                <span className="sm:hidden" aria-hidden>
                  {t('rejectShort')}
                </span>
                <span className="max-sm:hidden">{t('rejectNonEssential')}</span>
              </>
            )}
          </Button>
          <Button
            look="encuentro"
            variant="primary"
            onClick={showDetails ? consent.savePreferences : consent.acceptAll}
            className="max-sm:px-3"
          >
            {showDetails ? t('savePreferences') : t('acceptAll')}
          </Button>
        </div>
      </section>
    </div>
  )
}
